import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { apiFetch, ApiRequestError } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";

/**
 * Monitor → Watch Operations (ADR-0042 §8.5).
 *
 * Route: `/app/monitor/watches`
 *
 * Two modes:
 *  - **Browse all**: lists published watch operations with a selectable row
 *    that expands an inline history drawer of recent `atlas_watch_events`.
 *  - **Drilled-in**: when arriving with `?operation_id=<uuid>&watch_node_id=<id>`
 *    (the inspector's "View this watch's evaluation history" link), renders the
 *    history scoped to a single watch node, with a back link to that node in
 *    the operation editor. The all-ops list is hidden in this mode.
 */

type WatchOperation = {
  id: string;
  name: string;
  status: "draft" | "invalid" | "published" | "paused" | "validate";
  kind: string;
  updated_at: string;
};

type WatchEvent = {
  id: string;
  operation_id: string;
  watch_node_id: string;
  fired: boolean;
  trigger_context: Record<string, unknown>;
  response_results: Record<string, unknown>;
  dry_run: boolean;
  evaluated_at: string;
};

type WatchNodeMeta = {
  id: string;
  label: string;
};

type OperationDetail = {
  id: string;
  name: string;
  spec: { graph?: { nodes?: Array<{ id: string; type: string; label?: string }> } };
};

export function MonitorWatchOperations() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("operation_id") ?? null;
  const selectedWatchNodeId = searchParams.get("watch_node_id") ?? null;

  const toast = useToast();
  const [operations, setOperations] = useState<WatchOperation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [events, setEvents] = useState<WatchEvent[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventsError, setEventsError] = useState<string | null>(null);
  const [includeDryRun, setIncludeDryRun] = useState(true);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  /** When drilled into a single watch, the meta for the watch node from the
   *  operation spec — used for the page title and back link. Null until
   *  fetched, or when not drilled in. */
  const [watchNodeMeta, setWatchNodeMeta] = useState<WatchNodeMeta | null>(null);

  const loadOperations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ operations: WatchOperation[] }>(
        "/api/tenant/operations?kind=watch"
      );
      setOperations(res.operations);
    } catch (e) {
      setError(e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadOperations();
  }, [loadOperations]);

  const loadEvents = useCallback(
    async (operationId: string, watchNodeId: string | null, cursor: string | null = null, appending = false) => {
      setEventsLoading(true);
      setEventsError(null);
      try {
        const params = new URLSearchParams();
        params.set("limit", "50");
        params.set("include_dry_run", includeDryRun ? "true" : "false");
        if (watchNodeId) params.set("watch_node_id", watchNodeId);
        if (cursor) params.set("cursor", cursor);
        const res = await apiFetch<{ events: WatchEvent[]; nextCursor: string | null }>(
          `/api/tenant/operations/${encodeURIComponent(operationId)}/watch-events?${params.toString()}`
        );
        setEvents((prev) => (appending ? [...prev, ...res.events] : res.events));
        setNextCursor(res.nextCursor);
      } catch (e) {
        setEventsError(
          e instanceof ApiRequestError ? e.message : e instanceof Error ? e.message : String(e)
        );
      } finally {
        setEventsLoading(false);
      }
    },
    [includeDryRun]
  );

  useEffect(() => {
    if (selectedId) {
      void loadEvents(selectedId, selectedWatchNodeId);
    } else {
      setEvents([]);
      setNextCursor(null);
    }
  }, [selectedId, selectedWatchNodeId, loadEvents]);

  // When drilled into a specific watch node, fetch the operation spec to get
  // the node's label for the title.
  useEffect(() => {
    if (!selectedId || !selectedWatchNodeId) {
      setWatchNodeMeta(null);
      return;
    }
    void (async () => {
      try {
        const res = await apiFetch<OperationDetail>(
          `/api/tenant/operations/${encodeURIComponent(selectedId)}`
        );
        const node = res.spec?.graph?.nodes?.find((n) => n.id === selectedWatchNodeId);
        setWatchNodeMeta({
          id: selectedWatchNodeId,
          label: node?.label?.trim() || "Watch node"
        });
      } catch {
        setWatchNodeMeta({ id: selectedWatchNodeId, label: "Watch node" });
      }
    })();
  }, [selectedId, selectedWatchNodeId]);

  function selectOperation(id: string | null) {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (id) p.set("operation_id", id);
        else {
          p.delete("operation_id");
          p.delete("watch_node_id");
        }
        return p;
      },
      { replace: true }
    );
  }

  async function runDryRun(op: WatchOperation) {
    setBusy((prev) => ({ ...prev, [op.id]: true }));
    try {
      const res = await apiFetch<{ fired: boolean }>(
        `/api/tenant/operations/${encodeURIComponent(op.id)}/evaluate`,
        { method: "POST", body: { dry_run: true } }
      );
      toast.push({
        kind: res.fired ? "info" : "success",
        message: res.fired
          ? `Dry run: ${op.name} would fire.`
          : `Dry run: ${op.name} would not fire.`
      });
      if (selectedId === op.id) {
        await loadEvents(op.id, selectedWatchNodeId);
      }
    } catch (e) {
      toast.push({
        kind: "error",
        message: e instanceof Error ? e.message : String(e)
      });
    } finally {
      setBusy((prev) => ({ ...prev, [op.id]: false }));
    }
  }

  const selectedOp = selectedId ? operations.find((o) => o.id === selectedId) ?? null : null;
  const drilledIn = !!selectedId;

  // Page title and back link depend on whether we're drilled into a specific
  // watch node or just preselected an operation.
  const pageTitle = (() => {
    if (!drilledIn) return "Watch Operations";
    const opName = selectedOp?.name ?? "Watch Operation";
    if (selectedWatchNodeId && watchNodeMeta) {
      return `${opName} — ${watchNodeMeta.label}`;
    }
    return opName;
  })();

  const backHref = selectedWatchNodeId
    ? `/app/build/operations/${selectedId}?node=${encodeURIComponent(selectedWatchNodeId)}`
    : `/app/build/operations/${selectedId}`;
  const backLabel = selectedWatchNodeId ? "← Back to watch node" : "← Back to operation editor";

  return (
    <PageLayout title={pageTitle}>
      <div style={{ display: "grid", gap: 12 }}>
        {drilledIn && (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {selectedId && <Link to={backHref}>{backLabel}</Link>}
            <Link to="/app/monitor/watches" className="muted" style={{ marginLeft: "auto" }}>
              View all watch operations
            </Link>
          </div>
        )}

        {!drilledIn && (
          <div className="card">
            <div style={{ marginBottom: 10 }}>
              <span className="muted">
                Watch operations evaluate an upstream tabular result against a trigger condition and dispatch
                action items, emails, or webhooks when the condition holds. Pausing an operation stops new
                evaluations but does not close existing action items.
              </span>
            </div>

            {error && <ErrorState detail={error} />}
            {loading && !error && <LoadingState />}
            {!loading && !error && operations.length === 0 && (
              <div className="adminListEmpty">
                No watch operations yet.{" "}
                <Link to="/app/build/operations">Build one</Link> by creating an operation with a{" "}
                <span className="mono">watch</span> node.
              </div>
            )}
            {!loading && !error && operations.length > 0 && (
            <div className="tableWrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Operation</th>
                    <th>Status</th>
                    <th>Updated</th>
                    <th style={{ textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {operations.map((op) => {
                    const rowBusy = busy[op.id] ?? false;
                    const isSelected = op.id === selectedId;
                    return (
                      <tr
                        key={op.id}
                        style={{
                          background: isSelected ? "var(--bg-subtle)" : undefined,
                          cursor: "pointer"
                        }}
                        onClick={() => selectOperation(isSelected ? null : op.id)}
                      >
                        <td>
                          <div style={{ fontWeight: 600 }}>{op.name}</div>
                        </td>
                        <td>
                          {op.status === "published" ? (
                            <span className="pill pillOk">{op.status}</span>
                          ) : op.status === "paused" ? (
                            <span className="pill pillWarn">{op.status}</span>
                          ) : (
                            <span className="pill pillBad">{op.status}</span>
                          )}
                        </td>
                        <td className="muted">{new Date(op.updated_at).toLocaleString()}</td>
                        <td style={{ textAlign: "right" }} onClick={(e) => e.stopPropagation()}>
                          <div className="adminListActions">
                            <Link
                              className="adminListAction"
                              to={`/app/build/operations/${op.id}`}
                            >
                              Open editor
                            </Link>
                            <button
                              className="adminListAction"
                              type="button"
                              onClick={() => void runDryRun(op)}
                              disabled={rowBusy || op.status !== "published"}
                              title={op.status !== "published" ? "Publish to enable" : "Dry run"}
                            >
                              {rowBusy ? "…" : "Evaluate (dry run)"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}
          </div>
        )}

        {selectedOp && (
          <div className="card">
            <div style={{ display: "flex", alignItems: "center", marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 700 }}>
                  {selectedWatchNodeId && watchNodeMeta
                    ? `${watchNodeMeta.label} — evaluation history`
                    : `${selectedOp.name} — evaluation history`}
                </div>
                <div className="muted" style={{ fontSize: 13 }}>
                  Most recent 50 events.{" "}
                  {selectedWatchNodeId
                    ? "Scoped to this watch node only."
                    : "Across all watch nodes in this operation."}{" "}
                  Includes fires, non-fires, and (optionally) dry runs.
                </div>
              </div>
              <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
                <label
                  style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}
                >
                  <input
                    type="checkbox"
                    checked={includeDryRun}
                    onChange={(e) => setIncludeDryRun(e.target.checked)}
                  />
                  Include dry runs
                </label>
                <button
                  className="btn"
                  type="button"
                  onClick={() => void loadEvents(selectedOp.id, selectedWatchNodeId)}
                  disabled={eventsLoading}
                >
                  Refresh
                </button>
                <button className="btn" type="button" onClick={() => selectOperation(null)}>
                  Close
                </button>
              </div>
            </div>

            {eventsError && <ErrorState detail={eventsError} />}
            {eventsLoading && !eventsError && <LoadingState />}
            {!eventsLoading && !eventsError && events.length === 0 && (
              <div className="adminListEmpty">
                No events yet. Dry-run the watch from the editor or wait for the next upstream execution.
              </div>
            )}
            {!eventsLoading && !eventsError && events.length > 0 && (
              <>
                <div className="tableWrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>Evaluated</th>
                        <th>Alert created</th>
                        <th>Trigger context</th>
                        <th>Response results</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map((ev) => (
                        <tr key={ev.id}>
                          <td>
                            <div>{new Date(ev.evaluated_at).toLocaleString()}</div>
                            {ev.dry_run && (
                              <span className="pill pillWarn" style={{ marginTop: 2 }}>
                                dry run
                              </span>
                            )}
                          </td>
                          <td>
                            {isTrackingReset(ev.trigger_context) ? (
                              // Neither pill fits: nothing was evaluated, so "no"
                              // would read as "the watch checked and found nothing".
                              <span className="muted">—</span>
                            ) : (
                              <span
                                className={`pill ${ev.fired ? "pillOk" : "pillWarn"}`}
                                title={
                                  ev.fired
                                    ? "Action items, emails, or webhooks were dispatched this eval."
                                    : "The eval ran but no responses were dispatched (no matches, or all matches were already-firing identities)."
                                }
                              >
                                {ev.fired ? "yes" : "no"}
                              </span>
                            )}
                          </td>
                          <td className="muted" style={{ fontSize: 13 }}>
                            {summarizeTrigger(ev.trigger_context)}
                          </td>
                          <td className="muted" style={{ fontSize: 13 }}>
                            {isTrackingReset(ev.trigger_context) ? (
                              <span className="muted">—</span>
                            ) : ev.dry_run ? (
                              <span style={{ fontStyle: "italic" }}>skipped (dry run)</span>
                            ) : (
                              <>
                                {summarizeResponses(ev.response_results, ev.fired, ev.trigger_context)}
                                {firstResponseError(ev.response_results) && (
                                  <div
                                    className="mono"
                                    style={{
                                      fontSize: 12,
                                      color: "var(--danger-text)",
                                      marginTop: 4,
                                      whiteSpace: "pre-wrap",
                                      wordBreak: "break-word"
                                    }}
                                  >
                                    {firstResponseError(ev.response_results)}
                                  </div>
                                )}
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {nextCursor && (
                  <div style={{ marginTop: 10 }}>
                    <button
                      className="btn"
                      type="button"
                      onClick={() => void loadEvents(selectedOp.id, selectedWatchNodeId, nextCursor, true)}
                      disabled={eventsLoading}
                    >
                      Load more
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </PageLayout>
  );
}

/** ADR-0168 — a tracking reset is an event in this log, not an evaluation. */
export function isTrackingReset(ctx: Record<string, unknown> | undefined): boolean {
  return !!ctx && ctx.kind === "tracking_reset";
}

/**
 * The reset, as a sentence. Never the raw context, never an id: the actor is
 * the display name stored on the event, and a single-row reset names the row by
 * its own column values.
 */
export function summarizeTrackingReset(ctx: Record<string, unknown>): string {
  const who = typeof ctx.actor_display === "string" && ctx.actor_display.trim() ? ctx.actor_display : "someone";
  if (ctx.scope === "identity") {
    const values = ctx.identifying_values as Record<string, unknown> | null;
    const described = values
      ? Object.entries(values)
          .map(([col, val]) => `${col} ${val ?? "—"}`)
          .join(", ")
      : "one row";
    return `Tracking reset by ${who} for ${described || "one row"}`;
  }
  const cleared = numberOr(ctx.cleared_count, 0);
  return `Tracking reset by ${who} — ${cleared} ${cleared === 1 ? "row" : "rows"} cleared`;
}

function summarizeTrigger(ctx: Record<string, unknown>): string {
  if (isTrackingReset(ctx)) return summarizeTrackingReset(ctx);
  const matched = numberOr(ctx.matched_count, 0);
  const fired = numberOr(ctx.fired_count, 0);
  const autoResolved = numberOr(ctx.auto_resolved_count, 0);
  const dups = numberOr(ctx.duplicate_identity_count, 0);
  const parts: string[] = [];
  if (matched > 0 || fired > 0) {
    parts.push(`${matched} matched, ${fired} fired`);
  }
  if (autoResolved > 0) parts.push(`${autoResolved} auto-resolved`);
  if (dups > 0) parts.push(`${dups} duplicate identit${dups === 1 ? "y" : "ies"}`);
  return parts.join(" · ") || "—";
}

function summarizeResponses(
  results: Record<string, unknown>,
  fired: boolean,
  triggerContext: Record<string, unknown>
): string {
  const matched = numberOr(triggerContext.matched_count, 0);
  const autoResolved = numberOr(triggerContext.auto_resolved_count, 0);

  // Roll up dispatch outcomes (always — auto-resolves and refires can land
  // even when no fresh fire happens this eval).
  let created = 0;
  let refired = 0;
  let errors = 0;
  if (results && typeof results === "object") {
    for (const [, bucket] of Object.entries(results)) {
      if (!bucket || typeof bucket !== "object") continue;
      const b = bucket as Record<string, unknown>;
      created += numberOr(b.created, 0);
      refired += numberOr(b.refired, 0);
      errors += numberOr(b.error, 0);
    }
  }

  const parts: string[] = [];
  if (fired) {
    parts.push(`${created} item${created === 1 ? "" : "s"} created`);
    if (refired > 0) parts.push(`${refired} refired`);
    if (errors > 0) parts.push(`${errors} error${errors === 1 ? "" : "s"}`);
    return parts.join(" · ");
  }

  // Non-fire path. Surface useful state instead of a bare dash.
  if (autoResolved > 0) parts.push(`${autoResolved} auto-resolved`);
  if (errors > 0) parts.push(`${errors} error${errors === 1 ? "" : "s"}`);
  if (parts.length > 0) return parts.join(" · ");
  if (matched > 0) return "no new fires (still matching)";
  return "nothing matched";
}

function numberOr(v: unknown, fallback: number): number {
  return typeof v === "number" && Number.isFinite(v) ? v : fallback;
}

/** Returns the first non-empty `errors[0]` string across response buckets, or
 *  null. Used to surface dispatch-time failures inline so operators can
 *  diagnose without spelunking JSON. */
function firstResponseError(results: Record<string, unknown> | undefined): string | null {
  if (!results || typeof results !== "object") return null;
  for (const bucket of Object.values(results)) {
    if (!bucket || typeof bucket !== "object") continue;
    const errs = (bucket as Record<string, unknown>).errors;
    if (Array.isArray(errs) && errs.length > 0 && typeof errs[0] === "string") {
      return errs[0];
    }
  }
  return null;
}
